/**
 * Isomorphic error reporting helper.
 *
 * Use this inside any `catch` block that would otherwise swallow a failure
 * (toast-and-continue paths, background refreshes, optimistic UI rollbacks).
 * It routes to the browser SDK or the Worker-safe envelope reporter and never
 * throws, so it is always safe to call.
 */
export type ReportContext = {
  feature: string;
  action: string;
  /** Server-only: identity of the signed-in caller (from requireSupabaseAuth). */
  user?: { id?: string; email?: string | null; role?: string | null } | null;
  /** Ties this report to the browser action and its audit row. */
  correlationId?: string | null;
  [key: string]: unknown;
};

export async function reportHandled(error: unknown, context: ReportContext) {
  try {
    const { feature, action, user, correlationId, ...extra } = context;
    if (typeof window !== "undefined") {
      const { captureClientError } = await import("./sentry-client");
      await captureClientError(error, { feature, action, handled: true, ...extra });
      return;
    }
    const { captureServerException } = await import("./sentry.server");
    await captureServerException(error, {
      tags: {
        feature,
        action,
        handled: "true",
        ...(user?.role ? { app_role: user.role } : {}),
      },
      user: user ?? null,
      correlationId: correlationId ?? null,
      extra,
    });
  } catch {
    // Reporting must never break the caller.
  }
}


/** Fire-and-forget variant for use directly inside `catch` blocks. */
export function reportHandledSync(error: unknown, context: ReportContext) {
  void reportHandled(error, context);
}

/**
 * Raised when an alert-critical path completes "successfully" but produced the
 * wrong outcome (no email sent, zero recipients, empty message body). These are
 * the silent failures a plain try/catch never sees.
 */
export class OutcomeAssertionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OutcomeAssertionError";
  }
}

/**
 * Asserts an expected outcome on a critical path. When the assertion fails the
 * violation is reported to Sentry as a handled error; it never throws, so the
 * caller's flow is unchanged.
 */
export function expectOutcome(
  ok: boolean,
  context: ReportContext & { message?: string },
): boolean {
  if (ok) return true;
  const { message, ...rest } = context;
  reportHandledSync(
    new OutcomeAssertionError(message ?? `${context.feature}.${context.action} produced no result`),
    { ...rest, assertion: "outcome" },
  );
  return false;
}
